import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import type { NavItem } from '@frontend/nav-primitives';
import { Link } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import {
  NakedButton,
  NotificationBadge,
  PopoverMenu,
  PopoverMenuItem,
  styles,
  usePopoverMenu,
} from '@frontend/design-system';

type HeaderSubNavigationProps = {
  activePath: string;
  subNavItems: NavItem['subNavItems'];
  root: string;
  children: React.ReactNode;
};

export const HeaderSubNavigation = ({ root, activePath, subNavItems, children }: HeaderSubNavigationProps) => {
  const { getMenuProps, getTriggerProps, getItemProps, isOpen } = usePopoverMenu({
    placement: 'bottom-start',
    middlewareOptions: { offset: 4 },
  });

  return (
    <>
      <NakedButton
        css={css`
          display: flex;
          align-items: center;
          :hover .caret-icon {
            background: ${theme.colors.neutral20};
          }
          :focus .caret-icon {
            background: ${theme.colors.neutral20};
          }
        `}
        {...getTriggerProps()}
      >
        {children}
        <span
          css={[
            css`
              display: flex;
              align-items: center;
              margin-left: ${theme.spacing(2)};
              border-radius: ${theme.borderRadius.medium};
              justify-content: center;
              height: 24px;
              width: 24px;
              background: white;
              transition: background 150ms ease-out;
              color: ${theme.colors.neutral70};
              :hover,
              :focus {
                background: ${theme.colors.neutral20};
              }
            `,
          ]}
          style={{
            background: isOpen ? theme.colors.primary20 : undefined,
          }}
        >
          <Icon
            name='caret-down-small'
            css={css`
              transition: transform 150ms ease-out;
              transform: rotate(${isOpen ? '180deg' : '0deg'});
            `}
          />
        </span>
      </NakedButton>
      <PopoverMenu
        {...getMenuProps()}
        css={css`
          min-width: 220px;
          max-width: 300px;
        `}
      >
        {subNavItems?.map((subNavItem, index) => (
          <PopoverMenuItem
            preload={'intent'}
            preloadIntentProximity={100}
            //@ts-ignore
            //PopoverMenuItem isn't fylly polymorphic
            as={Link}
            key={subNavItem.path}
            css={css`
              color: ${theme.font.colors.default};
              justify-content: space-between;
              width: 100%;
            `}
            {...getItemProps({
              index,
            })}
            active={activePath === subNavItem.relativePath}
            to={subNavItem.path || `${root}/${subNavItem.relativePath}`}
          >
            <span css={styles.truncate}>{subNavItem.label}</span>
            <NavItemNotificationBadge count={subNavItem.count} isPassiveCount={subNavItem.isPassiveCount} />
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};

interface NavItemNotificationBadgeProps {
  count?: number;
  isPassiveCount?: boolean;
}

export const NavItemNotificationBadge = ({ count, isPassiveCount }: NavItemNotificationBadgeProps) => {
  return count ? (
    <NotificationBadge
      css={[
        css`
          /* we use important as PopoverMenuItem has styles with higher specificity */
          flex: unset !important;
          color: ${theme.colors.white} !important;
          margin-left: ${theme.spacing(1)};
        `,
        isPassiveCount && passiveBadgeStyles,
      ]}
      truncateCount
    >
      {count}
    </NotificationBadge>
  ) : null;
};

const passiveBadgeStyles = css`
  background: ${theme.colors.primary10};
  border-radius: ${theme.borderRadius.small};
  color: ${theme.colors.primary70} !important;
`;
